import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import logo from "../../resource/7.png";
import CommonFormFields from "./CommonFormFields";
import useFormHandler from "../../Hook/useFormHandler";

const InternalRetreatRequestForm = () => {
  const [searchParams] = useSearchParams();
  const retreatId = searchParams.get('retreatId');
  const retreatNo = searchParams.get('retreatNo'); // Get retreat number from URL
  
  const initialValues = {
    // Personal Information
    firstName: "",
    lastName: "",
    mobileNumber: "",
    wtNumber: "", // Maps to whatsapp_number
    email: "",
    gender: "",
    
    // Retreat Information
    internal_retreat_id: retreatId || "",
    retreat_no: retreatNo || "", // Show the retreat number
    interested_retreat_number: retreatNo || "",
    preferred_dates: "",
    
    // Additional Information
    queries: "",
    
    ip_address: ""
  };

  const validations = {
    // Personal Information
    firstName: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "First Name should contain only letters, spaces, or hyphens",
    },
    lastName: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "Last Name should contain only letters, spaces, or hyphens",
    },
    mobileNumber: {
      required: true,
      pattern: /^\+?\d{10,15}$/,
      message: "Mobile Number should be 10 to 15 digits, with optional '+'",
    },
    wtNumber: {
      required: true,
      pattern: /^\+?\d{10,15}$/,
      message: "WhatsApp Number should be 10 to 15 digits, with optional '+'",
    },
    email: {
      required: true,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Invalid email format",
    },
    gender: {
      required: true,
      validate: (value) =>
        ["male", "female"].includes(value) || "Please select a gender",
    },
    
    // Retreat Information
    interested_retreat_number: {
      required: true,
      message: "Retreat number is required",
    },
    preferred_dates: {
      required: true,
      minLength: 5,
      message: "Please specify your preferred dates",
    },
    
    // Additional Information
    queries: {
      required: false,
      maxLength: 1000,
      message: "Queries cannot exceed 1000 characters",
    },
  };

  const endpoint = '/create-internal-retreat-requests';

  const transformForApi = (data) => ({
    internal_retreat_id: parseInt(data.internal_retreat_id),
    retreat_no: data.retreat_no,
    first_name: data.firstName,
    last_name: data.lastName,
    mobile_number: data.mobileNumber,
    whatsapp_number: data.wtNumber,
    email_address: data.email,
    gender: data.gender,
    interested_retreat_number: data.interested_retreat_number,
    preferred_dates: data.preferred_dates.split(',').map(date => date.trim()).filter(date => date !== ''),
    queries: data.queries,
    ip_address: data.ip_address,
  });

  const {
    formData,
    errors,
    loading,
    handleInputChange,
    handleSubmit,
    setFormData,
  } = useFormHandler({
    initialValues,
    validations,
    endpoint,
    includeIP: true,
    transformFn: transformForApi
  });

  // Set retreat information when component mounts
  useEffect(() => {
    if (retreatId && retreatNo) {
      setFormData(prev => ({
        ...prev,
        internal_retreat_id: retreatId,
        retreat_no: retreatNo,
        interested_retreat_number: retreatNo
      }));
    }
  }, [retreatId, retreatNo, setFormData]);

  return (
    <div className="form-page dark-gold">
      <form onSubmit={handleSubmit}>
        <div className="form-title">
          <img src={logo} alt="Monastery Logo" width={200} />
          <div className="details">
            <h1 className="font-second">Bhaddeka Vihari Monastery Center</h1>
            <h2 className="h4">Internal Retreat Request Form</h2>
          </div>
        </div>

        <p className="font-p mt-6">
          Welcome to Bhaddeka Vihari Meditation Centre Internal Retreat Program
        </p>
        <p className="font-p mt-5">
          Please read the following information correctly. Before completing the
          retreat request form, you are required to review and understand the
          Rules and Regulations provided in the link. Please note that this is a
          request form only and does not serve as an official confirmation of
          registration for the internal retreat.
        </p>

        
        <p className="font-p mt-5">Personal Information *</p>
        <CommonFormFields
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
        />

        <p className="font-p mt-5">Gender *</p>
        <div className="gender-box">
          <div>
            <input
              type="radio"
              id="male"
              name="gender"
              value="male"
              checked={formData.gender === "male"}
              onChange={handleInputChange}
              className="mt-5"
            />
            <label htmlFor="male">Male</label>
          </div>
          <div>
            <input
              type="radio"
              id="female"
              name="gender"
              value="female"
              checked={formData.gender === "female"}
              onChange={handleInputChange}
              className="mt-5"
            />
            <label htmlFor="female">Female</label>
          </div>
        </div>
        {errors.gender && <span className="error">{errors.gender}</span>}

        <p className="font-p mt-5">
          Selected retreat number *
        </p>

        <div className="form-box1 mt-5">
          <div className="form-item">
            <input
              type="text"
              id="interested_retreat_number"
              name="interested_retreat_number"
              value={formData.interested_retreat_number}
              onChange={handleInputChange}
              disabled={true}
              className="disabled-input"
            />
            <label htmlFor="interested_retreat_number">Selected Retreat ID (Cannot be changed)</label>
            {errors.interested_retreat_number && <span className="error">{errors.interested_retreat_number}</span>}
          </div>
        </div>

        <p className="font-p mt-5">
          Please indicate the specific dates you wish to attend *
        </p>

        <div className="form-item mt-4">
          <textarea
            id="preferred_dates"
            name="preferred_dates"
            value={formData.preferred_dates}
            onChange={handleInputChange}
            rows="4"
          />
          <label htmlFor="preferred_dates">Preferred Dates
        
            Enter multiple dates separated by commas (e.g., 2024-03-15, 2024-03-16, 2024-03-17)
     </label>
          {errors.preferred_dates && <span className="error">{errors.preferred_dates}</span>}
        </div>

        <p className="font-p mt-5">
          Please write any queries you may have regarding the retreat event
        </p>

        <div className="form-item mt-4">
          <textarea
            id="queries"
            name="queries"
            value={formData.queries}
            onChange={handleInputChange}
            rows="6"
          />
          <label htmlFor="queries"></label>
          {errors.queries && <span className="error">{errors.queries}</span>}
        </div>

        <button
          type="submit"
          id="submitbtn"
          className="mt-5"
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit"}
        </button>

        <p className="font-p mt-5 pb-4">
          Upon the confirmation of your application, a unique identification
          number will be provided to facilitate your registration for the
          retreat
        </p>
        <p className="font-p pb-4">
          Thank you for your interest in participating in the Internal Retreat Program.
        </p>
      </form>
    </div>
  );
};

export default InternalRetreatRequestForm;